import { useState } from 'react'
import { Modal } from '../../components/Common/Modal'
import { Button } from '../../components/Common/Button'

const ENTITY_FIELDS = new Set(['name','type','entity_num','customer_code','registration_number','tax_number','industry','active'])
const ADDRESS_FIELDS = new Set(['address_line_1','address_line_2','suburb','city','postal_code','country'])
const CONTACT_FIELDS = new Set(['contact_person','phone','email','website'])
const BANKING_FIELDS = new Set(['account_name','account_type','bank_name','branch_name','branch_code','account_number'])

const AUTO_MAP = {
  'name': 'name',
  'entity name': 'name',
  'type': 'type',
  'entity number': 'entity_num',
  'entity num': 'entity_num',
  'entity_num': 'entity_num',
  'customer code': 'customer_code',
  'customer_code': 'customer_code',
  'registration number': 'registration_number',
  'registration_number': 'registration_number',
  'reg number': 'registration_number',
  'reg no': 'registration_number',
  'tax number': 'tax_number',
  'tax_number': 'tax_number',
  'industry': 'industry',
  'active': 'active',
  // address
  'address line 1': 'address_line_1',
  'address_line_1': 'address_line_1',
  'address 1': 'address_line_1',
  'address line 2': 'address_line_2',
  'address_line_2': 'address_line_2',
  'address 2': 'address_line_2',
  'suburb': 'suburb',
  'city': 'city',
  'postal code': 'postal_code',
  'postal_code': 'postal_code',
  'post code': 'postal_code',
  'country': 'country',
  // contact
  'contact person': 'contact_person',
  'contact_person': 'contact_person',
  'contact': 'contact_person',
  'phone': 'phone',
  'telephone': 'phone',
  'tel': 'phone',
  'email': 'email',
  'website': 'website',
  // banking
  'account name': 'account_name',
  'account_name': 'account_name',
  'account type': 'account_type',
  'account_type': 'account_type',
  'bank name': 'bank_name',
  'bank': 'bank_name',
  'branch name': 'branch_name',
  'branch_name': 'branch_name',
  'branch code': 'branch_code',
  'branch_code': 'branch_code',
  'account number': 'account_number',
  'account_number': 'account_number',
  'account no': 'account_number',
}

const COLUMN_OPTIONS = [
  { group: 'Entity Details', fields: [
    { value: 'name', label: 'Name *' },
    { value: 'type', label: 'Type' },
    { value: 'entity_num', label: 'Entity Number' },
    { value: 'customer_code', label: 'Customer Code' },
    { value: 'registration_number', label: 'Registration Number' },
    { value: 'tax_number', label: 'Tax Number' },
    { value: 'industry', label: 'Industry' },
    { value: 'active', label: 'Active' },
  ]},
  { group: 'Address', fields: [
    { value: 'address_line_1', label: 'Address Line 1' },
    { value: 'address_line_2', label: 'Address Line 2' },
    { value: 'suburb', label: 'Suburb' },
    { value: 'city', label: 'City' },
    { value: 'postal_code', label: 'Postal Code' },
    { value: 'country', label: 'Country' },
  ]},
  { group: 'Contact', fields: [
    { value: 'contact_person', label: 'Contact Person' },
    { value: 'phone', label: 'Phone' },
    { value: 'email', label: 'Email' },
    { value: 'website', label: 'Website' },
  ]},
  { group: 'Banking', fields: [
    { value: 'account_name', label: 'Account Name' },
    { value: 'account_type', label: 'Account Type' },
    { value: 'bank_name', label: 'Bank Name' },
    { value: 'branch_name', label: 'Branch Name' },
    { value: 'branch_code', label: 'Branch Code' },
    { value: 'account_number', label: 'Account Number' },
  ]},
]

export function EntityImport({ onImport, onClose, sidebarCollapsed }) {
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [columnMap, setColumnMap] = useState({})
  const [dragOver, setDragOver] = useState(false)

  const processFile = async (selectedFile) => {
    if (!selectedFile) return
    setError('')
    setFile(selectedFile)
    setLoading(true)
    try {
      const XLSX = await import('xlsx')
      const reader = new FileReader()
      reader.onload = (event) => {
        try {
          const data = event.target?.result
          const workbook = XLSX.read(data, { type: 'binary' })
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]
          const rows = XLSX.utils.sheet_to_json(worksheet)
          if (rows.length === 0) { setError('No data found in file'); setLoading(false); return }

          const headers = Object.keys(rows[0])
          const newMap = {}
          headers.forEach(header => {
            const matched = AUTO_MAP[header.toLowerCase().trim()]
            if (matched) newMap[header] = matched
          })
          setColumnMap(newMap)
          setPreview(rows.slice(0, 5))
        } catch (err) {
          setError(`Error reading file: ${err.message}`)
        } finally {
          setLoading(false)
        }
      }
      reader.onerror = () => { setError('Error reading file'); setLoading(false) }
      reader.readAsBinaryString(selectedFile)
    } catch (err) {
      setError(`Error loading Excel library: ${err.message}`)
      setLoading(false)
    }
  }

  const handleFileSelect = (e) => processFile(e.target.files?.[0])
  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true) }
  const handleDragLeave = () => setDragOver(false)
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); processFile(e.dataTransfer.files?.[0]) }

  const handleImport = async () => {
    if (!file || !preview) { setError('No file selected'); return }
    setError('')
    setLoading(true)
    try {
      const XLSX = await import('xlsx')
      const reader = new FileReader()
      reader.onload = async (event) => {
        try {
          const data = event.target?.result
          const workbook = XLSX.read(data, { type: 'binary' })
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]
          const rows = XLSX.utils.sheet_to_json(worksheet)

          const mapped = rows.map(row => {
            const flat = {}
            Object.entries(columnMap).forEach(([excelCol, dbCol]) => {
              if (!dbCol || dbCol === 'skip' || row[excelCol] === undefined) return
              if (dbCol === 'active') {
                flat[dbCol] = row[excelCol] === true || row[excelCol] === 'TRUE' || row[excelCol] === 'Yes' || row[excelCol] === 1
              } else {
                flat[dbCol] = String(row[excelCol]).trim()
              }
            })
            return flat
          }).filter(r => r.name)

          if (mapped.length === 0) {
            setError('No valid rows found. The Name column must be mapped and have values.')
            setLoading(false)
            return
          }

          // Split each flat row into entity / address / contact / banking
          const structured = mapped.map(flat => {
            const entityData = {}
            const addressData = {}
            const contactData = {}
            const bankingData = {}
            Object.entries(flat).forEach(([k, v]) => {
              if (ENTITY_FIELDS.has(k)) entityData[k] = v
              else if (ADDRESS_FIELDS.has(k)) addressData[k] = v
              else if (CONTACT_FIELDS.has(k)) contactData[k] = v
              else if (BANKING_FIELDS.has(k)) bankingData[k] = v
            })
            if (entityData.active === undefined) entityData.active = true
            return { entityData, addressData, contactData, bankingData }
          })

          // 90-second timeout — if session expired, queries can hang indefinitely
          const timeout = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Import timed out. Your session may have expired — please log out and log back in, then try again.')), 90000)
          )
          await Promise.race([onImport(structured), timeout])
          setLoading(false)
        } catch (err) {
          const msg = err.message || ''
          if (msg.toLowerCase().includes('refresh token') || msg.toLowerCase().includes('jwt') || msg.toLowerCase().includes('session')) {
            setError('Your session has expired. Please log out and log back in, then try the import again.')
          } else {
            setError(`Import error: ${msg}`)
          }
          setLoading(false)
        }
      }
      reader.onerror = () => { setError('Error reading file'); setLoading(false) }
      reader.readAsBinaryString(file)
    } catch (err) {
      setError(`Error: ${err.message}`)
      setLoading(false)
    }
  }

  const excelColumns = preview ? Object.keys(preview[0]) : []

  return (
    <Modal isOpen={true} onClose={onClose} title="Import Entities" sidebarCollapsed={sidebarCollapsed}>
      <div className="space-y-4 max-h-[80vh] flex flex-col">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {!preview ? (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragOver ? 'border-sfp-orange bg-orange-50' : 'border-gray-300 hover:border-sfp-orange'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="entity-file-input"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              disabled={loading}
              className="hidden"
            />
            <label htmlFor="entity-file-input" className="cursor-pointer">
              <p className="text-lg font-semibold text-sfp-darkgray mb-2">
                📁 Drop file here or click to select
              </p>
              <p className="text-sm text-gray-600">Supported: Excel (.xlsx, .xls) or CSV</p>
              {loading && <p className="text-sm text-sfp-orange mt-2">Loading...</p>}
            </label>
          </div>
        ) : (
          <div className="space-y-4 flex-1 overflow-y-auto">
            <div>
              <h3 className="font-semibold text-sm text-sfp-darkgray mb-3">
                Map Excel Columns to Database Fields
              </h3>
              <div className="space-y-2 max-h-52 overflow-y-auto">
                {excelColumns.map(col => (
                  <div key={col} className="flex gap-2 items-center text-sm">
                    <span className="flex-1 text-gray-700 truncate">{col}</span>
                    <span className="text-gray-400">→</span>
                    <select
                      value={columnMap[col] || ''}
                      onChange={(e) => setColumnMap(prev => ({ ...prev, [col]: e.target.value }))}
                      className="input-field flex-1 text-sm py-1"
                    >
                      <option value="">Skip</option>
                      {COLUMN_OPTIONS.map(group => (
                        <optgroup key={group.group} label={group.group}>
                          {group.fields.map(f => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-sm text-sfp-darkgray mb-2">Preview (first 5 rows)</h3>
              <div className="overflow-x-auto border border-gray-200 rounded max-h-40 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="table-header sticky top-0">
                    <tr>
                      {excelColumns.map(col => (
                        <th key={col} className="px-3 py-2 text-left whitespace-nowrap">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                        {excelColumns.map(col => (
                          <td key={col} className="px-3 py-2 text-gray-700 whitespace-nowrap">
                            {String(row[col] ?? '').substring(0, 40)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Showing first 5 rows · Existing entities matched by name will be updated
              </p>
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-end pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          {preview && (
            <>
              <Button
                variant="secondary"
                onClick={() => { setPreview(null); setFile(null); setColumnMap({}) }}
                type="button"
                disabled={loading}
              >
                Back
              </Button>
              <Button variant="primary" onClick={handleImport} disabled={loading}>
                {loading ? 'Importing...' : 'Import Entities'}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}
